/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  FiAlertCircle,
  FiArrowLeft,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiCopy,
  FiKey,
  FiLoader,
  FiMapPin,
  FiMoon,
  FiPrinter,
  FiRefreshCw,
  FiUsers,
  FiXCircle,
} from 'react-icons/fi';
import { MdOutlineBeachAccess } from 'react-icons/md';
import PackageState from '../../components/common/PackageState';
import PackageImage from '../../components/common/PackageImage';
import PackageFareSummary from '../../components/PackageFareSummary/PackageFareSummary';
import PackageBookingLookup from '../../components/PackageBookingLookup/PackageBookingLookup';
import { packageBookingDetailsApi } from '../../api/packagesApi';
import { PACKAGE_ROUTES } from '../../constants/packageConstants';
import {
  BOOKING_PAX_COLUMNS,
  PACKAGE_PAX_TYPES,
  PASSENGER_TYPE_LABEL,
  canSeePackageSupplier,
  daysNightsText,
  formatIsoDate,
  formatIsoDateTime,
  isPackageEnquiry,
  isPackageFailed,
  isPackagePending,
  packageImageUrl,
  packagePlace,
  packageStatusOf,
  readPackageBooking,
} from '../../utils/packageUtils';
import '../../styles/packagesShared.scss';
import './PackageBookingDetailsPage.scss';

function StatusPill({ status }) {
  const s = packageStatusOf(status);
  let modifier = 'pending';
  let Icon = FiClock;
  if (s === 'confirmed') {
    modifier = 'success';
    Icon = FiCheckCircle;
  } else if (isPackageFailed(s)) {
    modifier = 'error';
    Icon = FiXCircle;
  }
  return (
    <span className={`pkg-voucher__pill pkg-voucher__pill--${modifier}`}>
      <Icon /> {String(status || 'Pending').toUpperCase()}
    </span>
  );
}

function CopyRef({ label, value }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      // clipboard blocked — the value is still selectable on screen
    }
  };
  return (
    <div className="pkg-voucher__ref">
      <span className="pkg-voucher__ref-label">
        <FiKey /> {label}
      </span>
      <span className="pkg-voucher__ref-value">
        {value}
        <button
          type="button"
          onClick={copy}
          aria-label={`Copy ${label}`}
          title={copied ? 'Copied' : 'Copy'}
        >
          {copied ? <FiCheckCircle /> : <FiCopy />}
        </button>
      </span>
    </div>
  );
}

function Field({ label, value }) {
  if (value === null || value === undefined || String(value).trim() === '')
    return null;
  return (
    <div className="pkg-voucher__field">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function PackageBookingDetailsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { pkgBookingId: rawId } = useParams();
  const id = decodeURIComponent(rawId || '').trim();
  const justBooked = Boolean(location.state?.booked);
  // what the booking page knew, as a fallback while details fill in
  const fromBook = location.state?.booking || null;
  const fromSummary = location.state?.summary || null;

  const [state, setState] = useState({
    loading: true,
    read: null,
    booking: null,
    checkedAt: null,
    refreshError: '',
  });
  const [refreshing, setRefreshing] = useState(false);
  const [lookupOpen, setLookupOpen] = useState(false);

  const load = useCallback(
    async (signal, quiet = false) => {
      if (quiet) setRefreshing(true);
      else setState((s) => ({ ...s, loading: true }));
      const read = await packageBookingDetailsApi(id, signal);
      if (signal?.aborted) return;
      const booking = read.ok ? readPackageBooking(read.data) : null;
      setRefreshing(false);
      if (quiet && !booking) {
        setState((s) => ({
          ...s,
          refreshError: read.network
            ? 'Could not reach the server.'
            : read.message || 'Could not refresh the status.',
        }));
        return;
      }
      setState({
        loading: false,
        read,
        booking,
        checkedAt: new Date(),
        refreshError: '',
      });
    },
    [id],
  );

  useEffect(() => {
    if (!id) return undefined;
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [id, load]);

  if (!id) {
    return (
      <div className="pkg-voucher">
        <Container className="pkg-voucher__body">
          <PackageState
            title="No booking selected"
            primaryText="Search packages"
            onPrimary={() => navigate(PACKAGE_ROUTES.LANDING)}
          />
        </Container>
      </div>
    );
  }

  if (state.loading) {
    return (
      <div className="pkg-voucher">
        <section className="pkg-voucher__hero">
          <Container>
            <span className="pkg-voucher__hero-eyebrow">Package Booking</span>
            <h1 className="pkg-voucher__hero-title">
              <FiLoader className="pkg-spin" /> Loading booking {id}…
            </h1>
          </Container>
        </section>
        <Container className="pkg-voucher__body">
          <span className="pkg-skeleton pkg-skeleton--block" />
          <span className="pkg-skeleton pkg-skeleton--block" />
        </Container>
      </div>
    );
  }

  const { booking, read } = state;

  if (!booking) {
    return (
      <div className="pkg-voucher">
        <Container className="pkg-voucher__body">
          <PackageState
            title={
              read?.network
                ? 'We could not reach the server'
                : `Booking ${id} was not found`
            }
            message={
              read?.network
                ? 'Check your connection and try again.'
                : read?.message || 'Check the booking ID and try again.'
            }
            reference={read?.correlationId}
            primaryText="Try again"
            onPrimary={() => load()}
            secondaryText="Find another booking"
            onSecondary={() => setLookupOpen(true)}
          />
        </Container>
        <PackageBookingLookup
          show={lookupOpen}
          onHide={() => setLookupOpen(false)}
        />
      </div>
    );
  }

  const status = packageStatusOf(booking.status);
  const pending = isPackagePending(status);
  const failed = isPackageFailed(status);
  const confirmed = status === 'confirmed';
  const enquiry = isPackageEnquiry(status);
  const info = booking.info || {};
  const packageName =
    booking.packageName ||
    info.name ||
    fromBook?.packageName ||
    fromSummary?.name ||
    '';
  const cover = packageImageUrl(info.coverImage) || fromSummary?.cover || '';
  const place = packagePlace(info) || fromSummary?.destination || '';
  const days = Number(info.duration?.days) || fromSummary?.days || null;
  const nights = Number(info.duration?.nights) || fromSummary?.nights || 0;
  const departureDate =
    booking.departureDate || fromBook?.departure?.departureDate || '';
  const fare = booking.fare || fromBook?.fare || null;
  const hasFare = Number(fare?.grandTotal) > 0;
  const passengers = Array.isArray(booking.passengers)
    ? booking.passengers.filter(Boolean)
    : [];
  const paxRows = PACKAGE_PAX_TYPES.map((t) => ({
    ...t,
    count: Number(booking[BOOKING_PAX_COLUMNS[t.key]]) || 0,
  })).filter((r) => r.count > 0);

  let heroTitle = 'Booking received';
  if (confirmed) heroTitle = 'Your package is booked!';
  else if (pending) heroTitle = 'Booking is being confirmed';
  else if (failed) heroTitle = 'Booking was not confirmed';
  else if (enquiry) heroTitle = 'Enquiry sent';

  return (
    <main className="pkg-voucher">
      <section className="pkg-voucher__hero">
        <Container>
          <div className="pkg-voucher__hero-inner">
            <div>
              <span className="pkg-voucher__hero-eyebrow">
                Package Booking {enquiry ? 'Enquiry' : 'Confirmation'}
              </span>
              <h1 className="pkg-voucher__hero-title">
                {confirmed && <FiCheckCircle />}
                {heroTitle}
              </h1>
              {packageName && (
                <p className="pkg-voucher__hero-sub">{packageName}</p>
              )}
              <div className="pkg-voucher__hero-meta">
                <CopyRef
                  label="Booking ID"
                  value={booking.pkgBookingId || id}
                />
                {booking.bookingRef && (
                  <CopyRef label="Supplier Ref" value={booking.bookingRef} />
                )}
                {booking.partnerCreatedAt && (
                  <div className="pkg-voucher__ref">
                    <span className="pkg-voucher__ref-label">
                      <FiClock /> Booked on
                    </span>
                    <span className="pkg-voucher__ref-value">
                      {formatIsoDateTime(booking.partnerCreatedAt)}
                    </span>
                  </div>
                )}
                <StatusPill status={booking.status} />
              </div>
            </div>
            <div className="pkg-voucher__hero-actions">
              <button
                type="button"
                className="pkg-voucher__hero-btn"
                onClick={() => window.print()}
              >
                <FiPrinter /> Print
              </button>
            </div>
          </div>
        </Container>
      </section>

      <Container className="pkg-voucher__body">
        {justBooked && confirmed && (
          <div className="pkg-alert pkg-alert--success pkg-voucher__alert">
            <FiCheckCircle />
            <span>
              Booking confirmed and the amount has been debited from your
              wallet. Save the booking ID to open this page again.
            </span>
          </div>
        )}

        {pending && (
          <div className="pkg-alert pkg-alert--info pkg-voucher__alert">
            <FiClock />
            <span>
              The supplier is confirming this booking. Do not book it again.
              {state.checkedAt &&
                ` Last checked ${state.checkedAt.toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}.`}
              {state.refreshError && (
                <small className="d-block">{state.refreshError}</small>
              )}
            </span>
            <button
              type="button"
              className="pkg-alert__action"
              onClick={() => load(undefined, true)}
              disabled={refreshing}
            >
              {refreshing ? <FiLoader className="pkg-spin" /> : <FiRefreshCw />}{' '}
              Check status
            </button>
          </div>
        )}

        {failed && (
          <div className="pkg-alert pkg-voucher__alert">
            <FiAlertCircle />
            <span>
              The supplier did not confirm this booking. Contact support with
              the booking ID before booking again.
            </span>
          </div>
        )}

        <Row className="g-4">
          <Col lg={8} md={12}>
            <section className="pkg-voucher__card">
              <header className="pkg-voucher__card-head">
                <h3>
                  <MdOutlineBeachAccess /> Package
                </h3>
              </header>
              <div className="pkg-voucher__package">
                {cover && (
                  <span className="pkg-voucher__package-media">
                    <PackageImage src={cover} alt={packageName} />
                  </span>
                )}
                <div className="pkg-voucher__package-text">
                  <strong>{packageName || '—'}</strong>
                  <span className="pkg-voucher__package-meta">
                    {place && (
                      <span>
                        <FiMapPin /> {place}
                      </span>
                    )}
                    {days && (
                      <span>
                        <FiMoon /> {daysNightsText(days, nights)}
                      </span>
                    )}
                  </span>
                  {info.supplierName && canSeePackageSupplier() && (
                    <small>Operated by {info.supplierName}</small>
                  )}
                </div>
              </div>
            </section>

            <section className="pkg-voucher__card">
              <header className="pkg-voucher__card-head">
                <h3>
                  <FiCalendar /> Trip Details
                </h3>
              </header>
              <div className="pkg-voucher__grid">
                <Field label="Departure" value={formatIsoDate(departureDate)} />
                <Field
                  label="Return"
                  value={formatIsoDate(booking.returnDate)}
                />
                <Field
                  label="Joining city · grade"
                  value={[booking.joiningCityLabel, booking.grade]
                    .filter(Boolean)
                    .join(' · ')}
                />
                <Field label="Seats" value={booking.seats} />
                {paxRows.map((r) => (
                  <Field key={r.key} label={r.label} value={r.count} />
                ))}
                <Field
                  label="Balance due by"
                  value={formatIsoDate(booking.balanceDueDate)}
                />
                <Field label="Voucher code" value={booking.voucherCode} />
                <Field label="Departure ID" value={booking.fdId} />
                <Field label="Your reference" value={booking.clientBookingId} />
              </div>
              {booking.specialRequests && (
                <p className="pkg-voucher__requests">
                  <strong>Special requests:</strong> {booking.specialRequests}
                </p>
              )}
            </section>

            {passengers.length > 0 && (
              <section className="pkg-voucher__card">
                <header className="pkg-voucher__card-head">
                  <h3>
                    <FiUsers /> Travellers ({passengers.length})
                  </h3>
                </header>
                <ul className="pkg-voucher__pax">
                  {passengers.map((p, i) => {
                    const type = String(p.passengerType || '').toUpperCase();
                    return (
                      // eslint-disable-next-line react/no-array-index-key
                      <li key={i}>
                        <span className="pkg-voucher__pax-num">
                          {String(i + 1).padStart(2, '0')}
                        </span>
                        <span className="pkg-voucher__pax-text">
                          <strong>
                            {[p.title, p.firstName, p.lastName]
                              .filter(Boolean)
                              .join(' ')}
                            {p.isLead && <em>Lead</em>}
                          </strong>
                          <small>
                            {[
                              PASSENGER_TYPE_LABEL[type],
                              p.age != null && `${p.age} yrs`,
                              p.gender,
                              p.email,
                              p.phone,
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </small>
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}
          </Col>

          <Col lg={4} md={12}>
            {hasFare ? (
              <PackageFareSummary
                fare={fare}
                title="Payment Summary"
                paidLabel={
                  String(booking.paymentStatus).toLowerCase() === 'paid'
                    ? 'Total Paid'
                    : 'Total'
                }
              />
            ) : (
              <section className="pkg-voucher__card">
                <header className="pkg-voucher__card-head">
                  <h3>Payment Summary</h3>
                </header>
                <p className="pkg-voucher__muted">
                  {enquiry
                    ? 'Nothing has been charged. The supplier will share a price with you.'
                    : 'The fare for this booking is not available yet.'}
                </p>
              </section>
            )}
            {booking.paymentStatus && (
              <div className="pkg-voucher__payment">
                <span>Payment status</span>
                <strong>{String(booking.paymentStatus).toUpperCase()}</strong>
              </div>
            )}
            <button
              type="button"
              className="pkg-voucher__home-btn"
              onClick={() => navigate(PACKAGE_ROUTES.LANDING)}
            >
              <FiArrowLeft /> Book another package
            </button>
          </Col>
        </Row>
      </Container>
    </main>
  );
}

export default PackageBookingDetailsPage;
